import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { AlertTriangle, Sparkles, Check, X, Clock } from "lucide-react";
import { format, parseISO } from "date-fns";
import { pt } from "date-fns/locale";
import { api, formatApiError, BACKEND_URL } from "../../lib/api";

const EXTRA_LABELS = {
  lavandaria: "Lavandaria",
  consumiveis: "Consumíveis",
  limpeza_profunda: "Limpeza profunda",
  limpeza_exterior: "Limpeza exterior",
  relva: "Corte de relva",
  manutencao: "Manutenção",
};

const ISSUE_LABELS = {
  damage: "Danos no alojamento",
  consumables: "Falta de consumíveis",
  lightbulb: "Lâmpadas fundidas",
  maintenance: "Manutenção",
  extra_cleaning: "Limpeza extra",
  other: "Outro",
};

/**
 * ClientApprovals — pending items sent by admin: repairs (issues) + extras.
 * Client can Approve (auto-adds to invoice) or Reject. After each decision
 * admin + employee get notified.
 */
export default function ClientApprovals() {
  const [data, setData] = useState({ issues: [], extras: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/client/approvals");
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function decide(kind, id, decision) {
    setBusy(`${kind}-${id}-${decision}`);
    try {
      await api.post(`/client/approvals/${kind}/${id}`, { decision });
      toast.success(decision === "approve" ? "Aprovado. Será adicionado à sua fatura." : "Recusado.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  }

  const total = (data.issues?.length || 0) + (data.extras?.length || 0);

  return (
    <div className="space-y-8" data-testid="client-approvals">
      <header>
        <div className="eyebrow mb-3">A sua decisão é necessária</div>
        <h1 className="font-display text-5xl">Aprovações pendentes.</h1>
        <p className="text-sm text-[#a0b2af] mt-2 max-w-2xl">
          Reparações e serviços extras que o administrador preparou com base no trabalho
          dos funcionários. Se aprovar, o valor será adicionado à sua fatura.
        </p>
      </header>

      {loading && <div className="text-[#a0b2af] text-sm">A carregar…</div>}

      {!loading && total === 0 && (
        <div className="lux-card p-10 text-center text-sm text-[#a0b2af]" data-testid="approvals-empty">
          <Clock className="mx-auto mb-3 text-[#d4af37]" size={28} />
          Não há items pendentes de aprovação neste momento.
        </div>
      )}

      {data.issues?.length > 0 && (
        <section className="space-y-3">
          <div className="eyebrow text-[#fca5a5] flex items-center gap-2">
            <AlertTriangle size={14} /> Reparações
          </div>
          {data.issues.map(it => (
            <ApprovalCard key={it.id}
                          kind="issue"
                          title={ISSUE_LABELS[it.category] || it.category}
                          description={it.description}
                          adminNote={it.admin_note}
                          value={it.admin_value}
                          photos={it.photos || []}
                          createdAt={it.sent_to_client_at || it.created_at}
                          busy={busy}
                          id={it.id}
                          onDecide={decide} />
          ))}
        </section>
      )}

      {data.extras?.length > 0 && (
        <section className="space-y-3">
          <div className="eyebrow text-[#d4af37] flex items-center gap-2">
            <Sparkles size={14} /> Serviços extras sugeridos
          </div>
          {data.extras.map(it => (
            <ApprovalCard key={it.id}
                          kind="extra"
                          title={EXTRA_LABELS[it.extra_id] || it.extra_id}
                          description={it.note}
                          adminNote={it.admin_note}
                          value={it.admin_value}
                          photos={it.photos || []}
                          createdAt={it.sent_to_client_at || it.created_at}
                          busy={busy}
                          id={it.id}
                          onDecide={decide} />
          ))}
        </section>
      )}
    </div>
  );
}

function ApprovalCard({ kind, id, title, description, adminNote, value, photos, createdAt, busy, onDecide }) {
  const approving = busy === `${kind}-${id}-approve`;
  const rejecting = busy === `${kind}-${id}-reject`;
  return (
    <div className="lux-card p-5 border-l-4 border-[#d4af37]/50" data-testid={`approval-${kind}-${id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-widest text-[#d4af37]">{title}</div>
          {description && <div className="mt-2 text-sm">{description}</div>}
          {adminNote && (
            <div className="mt-2 text-xs text-[#fdfbf7] p-2 bg-[#062c27] border border-[#1a4640]">
              <span className="text-[#d4af37] uppercase tracking-widest text-[9px]">Nota do admin: </span>
              {adminNote}
            </div>
          )}
          <div className="mt-2 text-[11px] text-[#a0b2af]">
            Enviado em {format(parseISO(createdAt), "d 'de' MMM 'às' HH:mm", { locale: pt })}
          </div>
          {photos.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {photos.map(p => (
                <a key={p.id} href={`${BACKEND_URL}/api/files/${p.storage_path}`}
                   target="_blank" rel="noreferrer"
                   className="w-20 h-20 border border-[#1a4640] rounded-sm overflow-hidden hover:border-[#d4af37]">
                  <img src={`${BACKEND_URL}/api/files/${p.storage_path}`}
                       alt="foto" className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="font-display text-3xl text-[#d4af37]" data-testid={`approval-value-${kind}-${id}`}>
            {Number(value || 0).toFixed(2)} €
          </div>
          <div className="text-[10px] text-[#a0b2af] uppercase tracking-widest mt-1">A cobrar</div>
        </div>
      </div>
      <div className="flex gap-2 pt-4 mt-4 border-t border-[#1a4640]">
        <button onClick={() => onDecide(kind, id, "approve")}
                disabled={approving || rejecting}
                className="btn-gold px-5 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-60"
                data-testid={`approve-${kind}-${id}`}>
          <Check size={12} /> {approving ? "A aprovar…" : "Aprovar"}
        </button>
        <button onClick={() => onDecide(kind, id, "reject")}
                disabled={approving || rejecting}
                className="px-5 py-2 text-xs uppercase tracking-widest border border-[#fca5a5]/40 text-[#fca5a5] hover:bg-[#fca5a5]/10 inline-flex items-center gap-2 disabled:opacity-60"
                data-testid={`reject-${kind}-${id}`}>
          <X size={12} /> {rejecting ? "A recusar…" : "Recusar"}
        </button>
      </div>
    </div>
  );
}
